import { useTranslation } from "react-i18next";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@vapor/react-material";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";
import { Xmark } from "@vapor/react-icons";

interface TaxSubmissionDeleteModalProps {
    loading: boolean;
    open: boolean;
    onClose: () => void;
    onDelete: () => Promise<void>;
}

export const TaxSubmissionDeleteModal: React.FC<
    TaxSubmissionDeleteModalProps
> = ({ loading, open, onClose, onDelete }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { showTextMessage: showSuccessNotification } =
        useSuccessNotification();

    const handleOnClose = () => {
        onClose();
    };

    const handleOnDelete = async () => {
        try {
            await onDelete();
        } catch {
            // error notification already shown by handleHttpError
            return;
        }
        showSuccessNotification(
            t("views.home.components.tax_submission_delete_modal.success"),
        );
        handleOnClose();
    };

    return (
        <Dialog open={open} onClose={handleOnClose}>
            <DialogTitle>
                <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        width: "100%",
                    }}
                >
                    <Typography
                        color={theme.palette.primary.textTitleColor}
                        variant="bodyLarge"
                    >
                        {t(
                            "views.home.components.tax_submission_delete_modal.title",
                        )}
                    </Typography>
                    <IconButton
                        disabled={loading}
                        onClick={handleOnClose}
                        sx={{ padding: 0 }}
                    >
                        <Xmark color={loading ? "disabled" : "interactive"} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider variant="fullWidth" />
            <DialogContent>
                <Stack
                    justifyContent="space-between"
                    flexDirection="column"
                    gap={2}
                >
                    <Typography>
                        {t(
                            "views.home.components.tax_submission_delete_modal.description",
                        )}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={loading}
                    variant="outlined"
                    onClick={handleOnClose}
                >
                    {t(
                        "views.home.components.tax_submission_delete_modal.abort",
                    )}
                </Button>
                <Button
                    variant="contained"
                    disabled={loading}
                    onClick={handleOnDelete}
                >
                    {t(
                        "views.home.components.tax_submission_delete_modal.confirm",
                    )}
                    {loading && (
                        <CircularProgress
                            color="inherit"
                            sx={{ ml: 1 }}
                            size={16}
                        />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
